import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { map, take } from "rxjs/operators";
import { LoggedInGuard } from "../auth/auth.guards";
import { SessionService } from "../session.service";
import { PermissionService } from "../permission.service";
import { from, Observable } from "rxjs";
import { IntegrationsListComponent } from "./integrations-list/integrations-list.component";
import { IntegrationComponent } from "./integration/integration.component";
import { IntegrationPreviewComponent } from "./integration-preview/integration-preview.component";

import { IntegrationsService } from "./integrations.service";

@Injectable({
  providedIn: "root",
})
export class IntegrationWriteGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.integrations.hasWrite()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class IntegrationDetailGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.integrations.hasDetail()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class IntegrationListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(this.PermissionService.categories.integrations.hasList()).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({ providedIn: "root" })
class IntegrationsListPermissionResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}
  resolve() {
    return Promise.all([
      this.PermissionService.categories.integrations.hasWrite(),
      this.PermissionService.categories.integrations.hasDetail(),
    ]).then(([hasIntegrationsWrite, hasIntegrationsDetail]) => {
      return {
        hasIntegrationsWrite: hasIntegrationsWrite,
        hasIntegrationsDetail: hasIntegrationsDetail,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
class IntegrationPermissionResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}
  resolve() {
    return Promise.all([
      this.PermissionService.categories.facilities.hasList(),
    ]).then(([hasFacilitiesList]) => {
      return {
        hasFacilitiesList,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
export class IntegrationResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}
  resolve(route: ActivatedRouteSnapshot) {
    return this.IntegrationsService.getIntegration(
      this.SessionService.currentAccountValue!.accountID,
      route.params.integrationID,
      this.SessionService.currentAppValue!.id,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class IntegrationsResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    return this.IntegrationsService.getIntegrations(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      true
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class IntegrationProvidersResolve implements Resolve<any> {
  constructor(
    private IntegrationsService: IntegrationsService,
    private SessionService: SessionService,
  ) {}
  resolve() {
    return this.IntegrationsService.getIntegrationProviders(
      this.SessionService.currentAccountValue!.accountID
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard],
    runGuardsAndResolvers: "always",
    data: { sidebar: "integrations" },
    children: [
      {
        path: "list",
        component: IntegrationsListComponent,
        resolve: {
          permissions: IntegrationsListPermissionResolve,
        },
        data: { title: "Integrations" },
        canActivate: [IntegrationListGuard],
      },
      {
        path: "create",
        component: IntegrationComponent,
        resolve: {
          permissions: IntegrationPermissionResolve,
          providers: IntegrationProvidersResolve
        },
        data: { title: "Create New Integration" },
        canActivate: [IntegrationWriteGuard],
      },
      {
        path: ":integrationID",
        resolve: {
          integration: IntegrationResolve,
          permissions: IntegrationPermissionResolve,
          providers: IntegrationProvidersResolve
        },
        canActivate: [IntegrationWriteGuard, IntegrationDetailGuard],
        runGuardsAndResolvers: "paramsChange",
        children: [
          {
            path: "",
            component: IntegrationComponent,
            data: { title: "Integration" },
          },
          {
            path: "preview",
            component: IntegrationPreviewComponent,
            data: { title: "Preview Integration" },
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class IntegrationsRoutingModule {}

const integrationsModuleLazyLoadRoutes: Routes = [
  {
    path: "integrations",
    loadChildren: () =>
      import("./integrations.module").then((m) => m.IntegrationsModule),
  },
];

export { integrationsModuleLazyLoadRoutes as integrationsModuleLazyLoadRoutes };
